import { useEffect } from "react";

/**
 * Jumps to the section a cold-loaded URL asks for, so `/rsvp` or any
 * `#section` link lands where it should instead of at the top of the page.
 *
 * Three things make this less trivial than it looks:
 *
 *  - The browser's own anchor handling fires before React has rendered, so
 *    a cold `/#rsvp` lands at the top. We redo it after the first paint.
 *  - `behavior: "auto"` does NOT mean "jump". It defers to CSS, and this site
 *    sets `scroll-behavior: smooth`, which turns the landing into a slow
 *    animated scroll that Chrome's scroll restoration then cancels partway.
 *    `"instant"` is the one that actually jumps.
 *  - Cormorant Garamond changes nearly every line height on the page, so
 *    measuring before it loads lands the guest hundreds of pixels off.
 */
export function useLandingScroll(): void {
  useEffect(() => {
    const fromHash = window.location.hash.replace(/^#/, "");
    const fromPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
    const targetId = fromHash || fromPath;
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    // Stop the browser restoring the previous scroll position on top of ours.
    const previousRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";

    let cancelled = false;

    const jump = () => {
      if (cancelled) return;
      // "instant" rather than "auto": see the note above. `scroll-padding-top`
      // keeps the section clear of the sticky nav.
      target.scrollIntoView({ behavior: "instant", block: "start" });
    };

    // Land as soon as possible, then correct once fonts have settled and
    // again shortly after, in case a late image reflows the page above us.
    jump();
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    fontsReady.then(() => requestAnimationFrame(jump));
    const settle = setTimeout(jump, 400);

    return () => {
      cancelled = true;
      clearTimeout(settle);
      history.scrollRestoration = previousRestoration;
    };
  }, []);
}
